import React, { useState } from 'react';
import {
    Box, Grid, Typography, Stack, IconButton, Menu, MenuItem,
    Tabs, Tab, Divider, Paper, Button
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import GlassCard from '../ui/glass/components/GlassCard';
import { Loading } from '../components/common/Loading';

// Icons
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import EventIcon from '@mui/icons-material/Event';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PostAddIcon from '@mui/icons-material/PostAdd';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import HandymanIcon from '@mui/icons-material/Handyman';
import PaymentIcon from '@mui/icons-material/Payment';
import TodayIcon from '@mui/icons-material/Today';

// Modals
import CustomerFormModal from '../components/modals/CustomerFormModal';
import GeneralRandevuFormModal from '../components/modals/GeneralRandevuFormModal';
import PreInterviewFormModal from '../components/modals/PreInterviewFormModal';
import VadesiYaklasanlar from '../components/features/VadesiYaklasanlar';

// New Modular Components & Hooks
import {
    ServicePreviewCard,
    DuePaymentCard,
    TodaysPaymentRow,
    DailySummary,
    useDashboardData
} from '../modules/dashboard';

import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { findDuplicateDeviceBySerial, duplicateDeviceMessage } from '../utils/deviceDedup';

const Dashboard = () => {
    const { currentUser } = useAuth();
    const navigate = useNavigate();

    // Custom Hook for Data Fetching
    const {
        activeServices,
        dueServices,
        allTodaysPayments,
        upcomingPayments,
        todaysSleepAppointments,
        todaysServices,
        loading,
        debtors
    } = useDashboardData();

    // Modal States
    const [isCustomerModalOpen, setCustomerModalOpen] = useState(false);
    const [isRandevuModalOpen, setRandevuModalOpen] = useState(false);
    const [isPreInterviewModalOpen, setPreInterviewModalOpen] = useState(false);
    const [paymentTabValue, setPaymentTabValue] = useState(0);

    // Helper: Add Customer
    const handleAddCustomer = async (customerData, deviceInfo) => {
        try {
            // Mükerrer cihaz denetimi (normalize edilmiş) — müşteri oluşturulmadan önce.
            if (deviceInfo) {
                const duplicate = await findDuplicateDeviceBySerial(deviceInfo.seriNumarasi);
                if (duplicate) { alert(duplicateDeviceMessage(duplicate, deviceInfo.seriNumarasi)); return; }
            }
            const newCustomerRef = await addDoc(collection(db, "customers"), { ...customerData, eklenmeTarihi: serverTimestamp() });
            if (deviceInfo) {
                await addDoc(collection(db, "devices"), { ...deviceInfo, musteriId: newCustomerRef.id, musteriAdSoyad: customerData.adSoyad, eklenmeTarihi: serverTimestamp() });
            }
            alert("Müşteri başarıyla eklendi!");
        } catch (e) { console.error("Müşteri eklenirken hata: ", e); }
    };

    const todaysTotal = allTodaysPayments.reduce((sum, p) => sum + p.amount, 0);

    if (loading || !currentUser) return <Loading message="Pano yükleniyor..." />;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, height: '100%' }}>
            {/* Modals */}
            <CustomerFormModal isOpen={isCustomerModalOpen} onClose={() => setCustomerModalOpen(false)} onAddCustomer={handleAddCustomer} />
            <GeneralRandevuFormModal isOpen={isRandevuModalOpen} onClose={() => setRandevuModalOpen(false)} />
            <PreInterviewFormModal isOpen={isPreInterviewModalOpen} onClose={() => setPreInterviewModalOpen(false)} />

            {/* Quick Menu (Admin Only) */}
            {currentUser.role !== 'Servis Teknisyeni' && (
                <GlassCard sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>Hızlı Menü</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={6} md={2.4}><Button fullWidth size="large" variant="contained" color="secondary" startIcon={<SupportAgentIcon />} onClick={() => setPreInterviewModalOpen(true)} sx={{ whiteSpace: 'nowrap', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Ön Görüşme</Button></Grid>
                        <Grid item xs={6} md={2.4}><Button fullWidth size="large" variant="contained" color="info" startIcon={<EventIcon />} onClick={() => setRandevuModalOpen(true)} sx={{ whiteSpace: 'nowrap', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Randevu</Button></Grid>
                        <Grid item xs={6} md={2.4}><Button fullWidth size="large" variant="contained" color="success" startIcon={<PersonAddIcon />} onClick={() => setCustomerModalOpen(true)} sx={{ whiteSpace: 'nowrap', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Müşteri Ekle</Button></Grid>
                        <Grid item xs={6} md={2.4}><Button fullWidth size="large" variant="contained" color="primary" startIcon={<PostAddIcon />} onClick={() => navigate('/servis-kaydi-olustur')} sx={{ whiteSpace: 'nowrap', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Servis Kaydı</Button></Grid>
                        <Grid item xs={12} sm={12} md={2.4}><Button fullWidth size="large" variant="contained" color="warning" startIcon={<ShoppingCartIcon />} onClick={() => navigate('/satis-olustur')} sx={{ whiteSpace: 'nowrap', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Satış Oluştur</Button></Grid>
                    </Grid>
                </GlassCard>
            )}

            {/* Main Content Grid */}
            <Grid container spacing={3} sx={{ flexGrow: 1 }}>
                {currentUser.role === 'Servis Teknisyeni' ? (
                    // Technician View
                    <Grid item xs={12} sx={{ height: '100%' }}>
                        <GlassCard sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="h6" gutterBottom>Servisteki Aktif İşler ({activeServices.length})</Typography>
                            <Divider />
                            <Stack spacing={1.5} sx={{ mt: 2, flexGrow: 1, overflowY: 'auto' }}>
                                {activeServices.length > 0 ? activeServices.map(record => <ServicePreviewCard key={record.id} serviceRecord={record} />) : <Typography color="text.secondary">Serviste bekleyen cihaz yok.</Typography>}
                            </Stack>
                        </GlassCard>
                    </Grid>
                ) : (
                    // Admin/User View
                    <>
                        {/* Active Services */}
                        <Grid item xs={12} sm={6} lg={3}>
                            <GlassCard sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}><HandymanIcon color="primary" /><Typography variant="h6">Servisteki Cihazlar ({activeServices.length})</Typography></Box>
                                <Divider />
                                <Stack spacing={1.5} sx={{ mt: 2, flexGrow: 1, overflowY: 'auto', maxHeight: '300px' }}>
                                    {activeServices.length > 0 ? activeServices.slice(0, 10).map(record => <ServicePreviewCard key={record.id} serviceRecord={record} />) : <Typography color="text.secondary" sx={{ textAlign: 'center', pt: 2 }}>Serviste cihaz yok.</Typography>}
                                </Stack>
                            </GlassCard>
                        </Grid>

                        {/* Due Payments */}
                        <Grid item xs={12} sm={6} lg={3}>
                            <GlassCard sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <PaymentIcon color="error" />
                                    <Typography variant="h6">Ödeme / Alacaklar</Typography>
                                </Box>
                                <Tabs
                                    value={paymentTabValue}
                                    onChange={(e, v) => setPaymentTabValue(v)}
                                    variant="fullWidth"
                                    size="small"
                                    sx={{ minHeight: '36px', mb: 1, borderBottom: 1, borderColor: 'divider' }}
                                >
                                    <Tab label={`Servis (${dueServices.length})`} sx={{ minHeight: '36px', py: 0.5, fontSize: '0.75rem' }} />
                                    <Tab label={`Cari (${debtors.length})`} sx={{ minHeight: '36px', py: 0.5, fontSize: '0.75rem' }} />
                                </Tabs>

                                <Stack spacing={1} sx={{ mt: 1, flexGrow: 1, overflowY: 'auto', maxHeight: '350px', pr: 1 }}>
                                    {paymentTabValue === 0 ? (
                                        dueServices.length > 0 ? dueServices.slice(0, 10).map(record => <DuePaymentCard key={record.id} serviceRecord={record} />) : <Typography color="text.secondary" sx={{ textAlign: 'center', pt: 2, fontSize: '0.875rem' }}>Ödeme bekleyen servis kaydı yok.</Typography>
                                    ) : (
                                        debtors.length > 0 ? debtors.slice(0, 10).map(customer => (
                                            <Paper
                                                key={customer.id}
                                                elevation={0}
                                                sx={{
                                                    p: 1.5,
                                                    borderLeft: '4px solid #ef5350',
                                                    cursor: 'pointer',
                                                    backgroundColor: 'rgba(255, 255, 255, 0.5)',
                                                    transition: '0.2s',
                                                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.8)' },
                                                    borderRadius: 1,
                                                    mb: 0.5 // Add margin bottom to separate items
                                                }}
                                                onClick={() => navigate(`/musteri/${customer.id}`)}
                                            >
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <Box sx={{ overflow: 'hidden', flex: 1, mr: 1 }}>
                                                        <Typography variant="subtitle2" fontWeight="bold" noWrap title={customer.adSoyad}>
                                                            {customer.adSoyad}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary" display="block">
                                                            {customer.telefon || 'Telefon Yok'}
                                                        </Typography>
                                                    </Box>
                                                    <Typography variant="body2" color="error" fontWeight="bold" sx={{ whiteSpace: 'nowrap' }}>
                                                        {(customer.bakiye || 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
                                                    </Typography>
                                                </Box>
                                            </Paper>
                                        )) : <Typography color="text.secondary" sx={{ textAlign: 'center', pt: 2, fontSize: '0.875rem' }}>Borçlu müşteri bulunmuyor.</Typography>
                                    )}
                                </Stack>
                            </GlassCard>
                        </Grid>

                        {/* Today's Payments */}
                        <Grid item xs={12} sm={6} lg={3}>
                            <GlassCard sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <TodayIcon color="success" />
                                    <Typography variant="h6">Bugünkü Tahsilat</Typography>
                                </Box>
                                <Typography variant="h5" color="success.main" fontWeight="bold">
                                    {todaysTotal.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
                                </Typography>
                                <Divider sx={{ my: 1 }} />
                                <Stack spacing={1} sx={{ mt: 1, flexGrow: 1, overflowY: 'auto', maxHeight: '300px' }}>
                                    {allTodaysPayments.length > 0 ? allTodaysPayments.slice(0, 10).map((p, i) => <TodaysPaymentRow key={i} payment={p} />) : (
                                        <Typography color="text.secondary" sx={{ textAlign: 'center', pt: 1 }}>
                                            Bugün tahsilat yapılmadı.
                                        </Typography>
                                    )}
                                </Stack>
                            </GlassCard>
                        </Grid>

                        {/* Upcoming Due Dates */}
                        <Grid item xs={12} sm={6} lg={3}>
                            <VadesiYaklasanlar />
                        </Grid>
                    </>
                )}
            </Grid>

            {/* Daily Summary */}
            {currentUser.role !== 'Servis Teknisyeni' && (
                <DailySummary
                    upcomingPayments={upcomingPayments} // Updated prop
                    todaysSleepAppointments={todaysSleepAppointments}
                    todaysServices={todaysServices}
                />
            )}
        </Box>
    );
};

export default Dashboard;
